/* IntelApps — i18n Translations & Context */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';

export const translations = {
  en: {
    // Navigation
    nav: {
      work: 'Work',
      services: 'Services',
      process: 'Process',
      team: 'Team',
      insights: 'Insights',
      pricing: 'Pricing',
      book: 'Book a call',
    },
    // Common
    common: {
      booking: 'Available for new projects',
      readMore: 'Read more',
      learnMore: 'Learn more',
      backHome: '← Back home',
      viewAll: 'View all',
      contactUs: 'Contact us',
    },
    // Footer
    footer: {
      practice: 'Practice',
      company: 'Company',
      contact: 'Contact',
      tagline: 'A senior product studio for AI, mobile, web, and blockchain. Built to ship.',
      rights: '© 2026 IntelApps · All rights reserved',
      shipped: 'v3.0 · last shipped today',
    },
    // Hero Section
    hero: {
      studioInfo: 'Studio · NYC + Remote · est. 2018',
      title: 'We ship <span class="underline-mark">production-ready</span><br/>AI products in <span class="accent">12 weeks.</span>',
      description: 'A senior team for AI, mobile, web, and blockchain. From discovery to deployment — built to scale, priced to ship, delivered on time.',
      cta: {
        primary: 'Book a discovery call',
        secondary: 'View our work',
        responseTime: '~24h response',
      },
      badges: {
        soc2: 'SOC 2 Type II',
        iso: 'ISO 27001',
        gdpr: 'GDPR Compliant',
        aws: 'AWS Partner',
      },
      metrics: {
        tag: 'LIVE METRICS · Q2 2026',
        pill: 'On track',
        productsShipped: 'Products shipped',
        onTimeDelivery: 'On-time delivery',
        clientRevenue: 'Client revenue lifted',
        avgRating: 'Avg. client rating',
        throughput: 'weekly throughput · last 16 weeks',
      },
      trusted: {
        label: 'Trusted by 50+ teams worldwide',
      },
    },
    // Stats Section
    stats: {
      eyebrow: 'Outcomes',
      title: 'Results that compound — measured, not claimed.',
      launches: {
        num: '50',
        unit: '+',
        label: 'Production launches across',
        sublabel: 'AI, mobile, web & DApps',
      },
      revenue: {
        num: '$24',
        unit: 'M',
        label: 'Revenue generated for clients',
        sublabel: 'in the last 24 months',
      },
      delivery: {
        num: '12',
        unit: 'w',
        label: 'Average concept-to-launch',
        sublabel: 'delivery window',
      },
    },
    // Why choose us
    whyUs: {
      eyebrow: 'Why IntelApps',
      title: 'Why choose us',
      description:
        'Senior engineers on your roadmap — not a rotating bench behind an account manager.',
      reasons: [
        {
          title: 'Shipped, not slid',
          desc: 'We measure success in production releases, store listings, and live SLAs — not slide decks.',
        },
        {
          title: 'AI built into the product',
          desc: 'LLMs, agents, and classical ML where they cut cost or unlock revenue — integrated, evaluated, and owned.',
        },
        {
          title: 'One team, end to end',
          desc: 'Design, backend, and AI share the same sprint and standups — fewer handoffs and fewer surprises.',
        },
        {
          title: 'Predictable cadence',
          desc: 'Phased contracts with weekly demos. You always see what shipped, what is in flight, and what is next.',
        },
      ],
    },
    // Services Section
    services: {
      eyebrow: 'Services',
      title: 'Our Services',
      description: 'Comprehensive digital solutions tailored to your business needs.',
      items: {
        web: {
          title: 'Web Development',
          desc: 'Custom websites, web applications, and e-commerce solutions built with modern technologies for optimal performance and user experience.',
        },
        mobile: {
          title: 'Mobile Apps',
          desc: 'Native and cross-platform mobile applications for iOS and Android with seamless user experiences and offline capabilities.',
        },
        design: {
          title: 'UI/UX Design',
          desc: 'User-centered design solutions that combine aesthetics with functionality to create engaging digital experiences.',
        },
        ai: {
          title: 'AI / ML',
          desc: 'Production AI: custom models, LLM agents, RAG over your data, evals, and guardrails — integrated into your product, not a slide deck.',
        },
        blockchain: {
          title: 'Blockchain',
          desc: 'Smart contracts, on-chain integrations, and production infrastructure — designed for security, correctness, and real-world usage.',
        },
        seo: {
          title: 'SEO Optimization',
          desc: 'Search engine optimization services to improve your website rankings, increase organic traffic, and enhance online visibility.',
        },
      },
    },
    // Process Section
    process: {
      eyebrow: 'Process',
      title: 'Our Process',
      description: 'A streamlined approach to delivering exceptional digital solutions on time and within budget.',
      phases: {
        discovery: {
          name: 'Discovery',
          time: '1 week',
          desc: 'Understanding your business goals, target audience, and project requirements through research and analysis.',
          items: ['Research', 'Analysis', 'Requirements'],
        },
        planning: {
          name: 'Planning',
          time: '2 weeks',
          desc: 'Creating a detailed project roadmap, wireframes, and strategic plan to guide development process.',
          items: ['Strategy', 'Wireframes', 'Roadmap'],
        },
        execution: {
          name: 'Execution',
          time: '6 weeks',
          desc: 'Agile development with regular updates, testing, and iterations to bring your vision to life.',
          items: ['Development', 'Testing', 'Iterations'],
        },
        launch: {
          name: 'Launch',
          time: '1 week',
          desc: 'Final deployment, quality assurance, and post-launch support to ensure successful project delivery.',
          items: ['Deployment', 'QA', 'Support'],
        },
      },
      phaseLabel: 'PHASE',
    },
    // Portfolio Section
    portfolio: {
      eyebrow: 'Selected work',
      title: 'Real products. Real metrics. No mockups.',
      description: 'A subset of recent launches — apps in stores, platforms in production, contracts on mainnet.',
      filters: {
        all: 'All work',
        ai: 'AI',
        mobile: 'Mobile',
        web: 'Web',
        blockchain: 'Blockchain',
      },
      caseStudy: {
        tag: '// CASE STUDY · 2026',
        title: 'Winify — AI agent that automates RFP responses',
        meta: 'AI · Web · 14 weeks',
        quote: 'Cut RFP turnaround from 11 days to under 6 hours. The team understood enterprise procurement better than our last vendor.',
        stats: {
          timeSaved: { num: '96%', lbl: 'Time saved' },
          winRate: { num: '3.2×', lbl: 'Win-rate' },
          pipeline: { num: '$4M', lbl: 'Pipeline unlocked' },
        },
      },
      clients: {
        eyebrow: 'Clients',
        title: 'What founders say after we ship.',
      },
    },
    // Testimonials Section
    testimonials: {
      project: 'PROJECT',
      timeline: 'TIMELINE',
      outcome: 'OUTCOME',
    },
    // FAQ Section
    faq: {
      eyebrow: 'FAQ',
      title: 'Common questions, plain answers.',
      description: "Don't see your question? Drop us a line — we reply inside 24 hours.",
      cta: 'Ask us anything',
      items: {
        q1: {
          q: 'How long does a typical project take?',
          a: 'Most engagements run 10–14 weeks from kickoff to production. We commit to a delivery date in week one of discovery, and we hit it 98% of the time.',
        },
        q2: {
          q: 'What does a project cost?',
          a: 'MVP engagements start at $50K. Full product builds typically land between $80K–$200K. We send a fixed-fee SOW after discovery — no hourly billing.',
        },
        q3: {
          q: 'Do you work with startups, scaleups, or enterprise?',
          a: 'All three. Roughly half of our work is Series A–C product teams; the other half is enterprise innovation groups.',
        },
        q4: {
          q: 'Who actually does the work?',
          a: "A senior engineer, a product designer, and a delivery PM as a minimum. Everyone you meet in the kickoff is on the team — we don't bait-and-switch.",
        },
        q5: {
          q: 'What happens after launch?',
          a: 'A 30-day production warranty is included. After that, optional retainers or a clean handoff package.',
        },
        q6: {
          q: 'Do you sign NDAs?',
          a: "Yes — happy to use yours, or we'll send ours.",
        },
      },
    },
    // Discuss CTA Section
    discuss: {
      eyebrow: "Let's talk",
      title: 'Ready to build <span class="accent">something real?</span>',
      description: "Tell us a little about the project. We'll reply within 24 hours with next steps and a ballpark cost.",
      cta: 'Send a project brief',
      email: 'developer@intelapps.ai',
    },
    // 404 Page
    notFound: {
      eyebrow: '— / 404',
      title: 'Page not found.',
      description: "The page you're looking for doesn't exist.",
      backHome: '← Back home',
    },
  },
  es: {
    // Navigation
    nav: {
      work: 'Proyectos',
      services: 'Servicios',
      process: 'Proceso',
      team: 'Equipo',
      insights: 'Ideas',
      pricing: 'Precios',
      book: 'Agendar llamada',
    },
    // Common
    common: {
      booking: 'Disponible para nuevos proyectos',
      readMore: 'Leer más',
      learnMore: 'Más información',
      backHome: '← Volver al inicio',
      viewAll: 'Ver todo',
      contactUs: 'Contáctanos',
    },
    // Footer
    footer: {
      practice: 'Áreas',
      company: 'Compañía',
      contact: 'Contacto',
      tagline: 'Un estudio sénior de producto para IA, móvil, web y blockchain. Hecho para enviar.',
      rights: '© 2026 IntelApps · Todos los derechos reservados',
      shipped: 'v3.0 · último envío hoy',
    },
    // Hero Section
    hero: {
      studioInfo: 'Estudio · NYC + Remoto · est. 2018',
      title: 'Entregamos productos de IA <span class="underline-mark">listos para producción</span><br/>en <span class="accent">12 semanas.</span>',
      description: 'Un equipo senior para IA, móvil, web y blockchain. Del descubrimiento al despliegue — construido para escalar, con precios para enviar, entregado a tiempo.',
      cta: {
        primary: 'Reservar una llamada de descubrimiento',
        secondary: 'Ver nuestro trabajo',
        responseTime: '~24h de respuesta',
      },
      badges: {
        soc2: 'SOC 2 Type II',
        iso: 'ISO 27001',
        gdpr: 'Cumple con GDPR',
        aws: 'Socio AWS',
      },
      metrics: {
        tag: 'MÉTRICAS EN VIVO · Q2 2026',
        pill: 'En camino',
        productsShipped: 'Productos enviados',
        onTimeDelivery: 'Entrega a tiempo',
        clientRevenue: 'Ingresos de clientes aumentados',
        avgRating: 'Calificación promedio de clientes',
        throughput: 'rendimiento semanal · últimas 16 semanas',
      },
      trusted: {
        label: 'Confían más de 50 equipos en todo el mundo',
      },
    },
    // Stats Section
    stats: {
      eyebrow: 'Resultados',
      title: 'Resultados que se multiplican — medidos, no declarados.',
      launches: {
        num: '50',
        unit: '+',
        label: 'Lanzamientos de producción en',
        sublabel: 'IA, móvil, web y DApps',
      },
      revenue: {
        num: '$24',
        unit: 'M',
        label: 'Ingresos generados para clientes',
        sublabel: 'en los últimos 24 meses',
      },
      delivery: {
        num: '12',
        unit: 's',
        label: 'Ventana promedio de',
        sublabel: 'entrega de concepto a lanzamiento',
      },
    },
    // Why choose us
    whyUs: {
      eyebrow: 'Por qué IntelApps',
      title: 'Por qué elegirnos',
      description:
        'Ingenieros senior en su hoja de ruta — no un banco rotativo detrás de un gestor de cuentas.',
      reasons: [
        {
          title: 'En producción, no en diapositivas',
          desc: 'Medimos el éxito en lanzamientos a producción, tiendas y SLA en vivo — no en presentaciones.',
        },
        {
          title: 'IA dentro del producto',
          desc: 'LLM, agentes y ML clásico donde reducen coste o generan ingresos — integrados, evaluados y operados.',
        },
        {
          title: 'Un solo equipo, de punta a punta',
          desc: 'Diseño, backend e IA comparten el mismo sprint y dailies — menos relevos y menos sorpresas.',
        },
        {
          title: 'Ritmo predecible',
          desc: 'Contratos por fases con demos semanales. Siempre ve qué salió, qué está en curso y qué sigue.',
        },
      ],
    },
    // Services Section
    services: {
      eyebrow: 'Servicios',
      title: 'Nuestros Servicios',
      description: 'Soluciones digitales integrales adaptadas a las necesidades de su negocio.',
      items: {
        web: {
          title: 'Desarrollo Web',
          desc: 'Sitios web personalizados, aplicaciones web y soluciones de comercio electrónico construidas con tecnologías modernas para un rendimiento óptimo y experiencia del usuario.',
        },
        mobile: {
          title: 'Aplicaciones Móviles',
          desc: 'Aplicaciones móviles nativas y multiplataforma para iOS y Android con experiencias de usuario perfectas y capacidades sin conexión.',
        },
        design: {
          title: 'Diseño UI/UX',
          desc: 'Soluciones de diseño centradas en el usuario que combinan estética y funcionalidad para crear experiencias digitales atractivas.',
        },
        ai: {
          title: 'IA / ML',
          desc: 'IA en producción: modelos a medida, agentes LLM, RAG sobre sus datos, evaluaciones y salvaguardas — integrados en su producto, no solo en presentaciones.',
        },
        blockchain: {
          title: 'Blockchain',
          desc: 'Contratos inteligentes, integraciones on-chain e infraestructura de producción — diseñados para seguridad, corrección y uso real.',
        },
        seo: {
          title: 'Optimización SEO',
          desc: 'Servicios de optimización de motores de búsqueda para mejorar sus clasificaciones, aumentar el tráfico orgánico y mejorar la visibilidad en línea.',
        },
      },
    },
    // Process Section
    process: {
      eyebrow: 'Proceso',
      title: 'Nuestro Proceso',
      description: 'Un enfoque optimizado para entregar soluciones digitales excepcionales a tiempo y dentro del presupuesto.',
      phases: {
        discovery: {
          name: 'Descubrimiento',
          time: '1 semana',
          desc: 'Comprender sus objetivos comerciales, público objetivo y requisitos del proyecto a través de investigación y análisis.',
          items: ['Investigación', 'Análisis', 'Requisitos'],
        },
        planning: {
          name: 'Planificación',
          time: '2 semanas',
          desc: 'Crear un mapa de proyecto detallado, wireframes y un plan estratégico para guiar el proceso de desarrollo.',
          items: ['Estrategia', 'Wireframes', 'Hoja de ruta'],
        },
        execution: {
          name: 'Ejecución',
          time: '6 semanas',
          desc: 'Desarrollo ágil con actualizaciones regulares, pruebas e iteraciones para dar vida a su visión.',
          items: ['Desarrollo', 'Pruebas', 'Iteraciones'],
        },
        launch: {
          name: 'Lanzamiento',
          time: '1 semana',
          desc: 'Despliegue final, aseguramiento de calidad y soporte posterior al lanzamiento para garantizar una entrega exitosa del proyecto.',
          items: ['Despliegue', 'QA', 'Soporte'],
        },
      },
      phaseLabel: 'FASE',
    },
    // Portfolio Section
    portfolio: {
      eyebrow: 'Trabajo seleccionado',
      title: 'Productos reales. Métricas reales. No maquetas.',
      description: 'Un subconjunto de lanzamientos recientes — aplicaciones en tiendas, plataformas en producción, contratos en mainnet.',
      filters: {
        all: 'Todo el trabajo',
        ai: 'IA',
        mobile: 'Móvil',
        web: 'Web',
        blockchain: 'Blockchain',
      },
      caseStudy: {
        tag: '// ESTUDIO DE CASO · 2026',
        title: 'Winify — Agente IA que automatiza respuestas de RFP',
        meta: 'IA · Web · 14 semanas',
        quote: 'Redució el tiempo de respuesta de RFP de 11 días a menos de 6 horas. El equipo entendió la adquisición empresarial mejor que nuestro último proveedor.',
        stats: {
          timeSaved: { num: '96%', lbl: 'Tiempo ahorrado' },
          winRate: { num: '3.2×', lbl: 'Tasa de éxito' },
          pipeline: { num: '$4M', lbl: 'Cartera desbloqueada' },
        },
      },
      clients: {
        eyebrow: 'Clientes',
        title: 'Lo que dicen los fundadores después de que enviamos.',
      },
    },
    // Testimonials Section
    testimonials: {
      project: 'PROYECTO',
      timeline: 'CRONOLOGÍA',
      outcome: 'RESULTADO',
    },
    // FAQ Section
    faq: {
      eyebrow: 'Preguntas frecuentes',
      title: 'Preguntas comunes, respuestas sencillas.',
      description: '¿No ves tu pregunta? Escríbenos — respondemos en 24 horas.',
      cta: 'Pregúntanos cualquier cosa',
      items: {
        q1: {
          q: '¿Cuánto tiempo toma un proyecto típico?',
          a: 'La mayoría de los compromisos duran 10–14 semanas desde el inicio hasta la producción. Nos comprometemos con una fecha de entrega en la primera semana del descubrimiento, y la cumplimos el 98% de las veces.',
        },
        q2: {
          q: '¿Cuánto cuesta un proyecto?',
          a: 'Los compromisos de MVP comienzan en $50K. Las construcciones de productos completas típicamente están entre $80K–$200K. Enviamos un SOW de tarifa fija después del descubrimiento — sin facturación por horas.',
        },
        q3: {
          q: '¿Trabajan con startups, empresas en crecimiento o grandes empresas?',
          a: 'Las tres. Aproximadamente la mitad de nuestro trabajo son equipos de producto de Serie A–C; la otra mitad son grupos de innovación empresarial.',
        },
        q4: {
          q: '¿Quién hace realmente el trabajo?',
          a: 'Como mínimo, un ingeniero senior, un diseñador de productos y un PM de entrega. Todos los que conoces en el inicio están en el equipo — no hacemos cebo y cambio.',
        },
        q5: {
          q: '¿Qué sucede después del lanzamiento?',
          a: 'Se incluye una garantía de producción de 30 días. Después de eso, honorarios opcionales o un paquete de entrega limpio.',
        },
        q6: {
          q: '¿Firman acuerdos de confidencialidad?',
          a: 'Sí — nos encanta usar el tuyo, o enviaremos el nuestro.',
        },
      },
    },
    // Discuss CTA Section
    discuss: {
      eyebrow: 'Hablemos',
      title: '¿Listo para construir <span class="accent">algo real?</span>',
      description: 'Cuéntanos un poco sobre el proyecto. Responderemos en 24 horas con los próximos pasos y un costo estimado.',
      cta: 'Enviar breve del proyecto',
      email: 'developer@intelapps.ai',
    },
    // 404 Page
    notFound: {
      eyebrow: '— / 404',
      title: 'Página no encontrada.',
      description: 'La página que buscas no existe.',
      backHome: '← Volver al inicio',
    },
  },
  fr: {
    // Navigation
    nav: {
      work: 'Projets',
      services: 'Services',
      process: 'Processus',
      team: 'Équipe',
      insights: 'Articles',
      pricing: 'Tarifs',
      book: 'Réserver un appel',
    },
    // Common
    common: {
      booking: 'Disponible pour nouveaux projets',
      readMore: 'En savoir plus',
      learnMore: 'En savoir plus',
      backHome: '← Retour à l\'accueil',
      viewAll: 'Voir tout',
      contactUs: 'Contactez-nous',
    },
    // Footer
    footer: {
      practice: 'Pratiques',
      company: 'Société',
      contact: 'Contact',
      tagline: 'Un studio produit senior pour l\'IA, le mobile, le web et la blockchain. Fait pour livrer.',
      rights: '© 2026 IntelApps · Tous droits réservés',
      shipped: 'v3.0 · dernier déploiement aujourd\'hui',
    },
    // Hero Section
    hero: {
      studioInfo: 'Studio · NYC + Distant · est. 2018',
      title: 'Nous livrons des produits IA <span class="underline-mark">prêts pour la production</span><br/>en <span class="accent">12 semaines.</span>',
      description: 'Une équipe senior pour l\'IA, le mobile, le web et la blockchain. De la découverte au déploiement — construit pour évoluer, tarifé pour livrer, livré à temps.',
      cta: {
        primary: 'Réserver un appel de découverte',
        secondary: 'Voir notre travail',
        responseTime: '~24h de réponse',
      },
      badges: {
        soc2: 'SOC 2 Type II',
        iso: 'ISO 27001',
        gdpr: 'Conforme GDPR',
        aws: 'Partenaire AWS',
      },
      metrics: {
        tag: 'MÉTRIQUES EN DIRECT · Q2 2026',
        pill: 'Sur la bonne voie',
        productsShipped: 'Produits livrés',
        onTimeDelivery: 'Livraison à temps',
        clientRevenue: 'Chiffre d\'affaires client augmenté',
        avgRating: 'Note client moyenne',
        throughput: 'débit hebdomadaire · dernières 16 semaines',
      },
      trusted: {
        label: 'Approuvé par plus de 50 équipes dans le monde',
      },
    },
    // Stats Section
    stats: {
      eyebrow: 'Résultats',
      title: 'Des résultats qui se composent — mesurés, non déclarés.',
      launches: {
        num: '50',
        unit: '+',
        label: 'Lancements de production sur',
        sublabel: 'IA, mobile, web et DApps',
      },
      revenue: {
        num: '$24',
        unit: 'M',
        label: 'Chiffre d\'affaires généré pour les clients',
        sublabel: 'au cours des 24 derniers mois',
      },
      delivery: {
        num: '12',
        unit: 's',
        label: 'Délai moyen de',
        sublabel: 'concept au lancement',
      },
    },
    // Why choose us
    whyUs: {
      eyebrow: 'Pourquoi IntelApps',
      title: 'Pourquoi nous choisir',
      description:
        'Des ingénieurs seniors sur votre roadmap — pas une équipe tournante derrière un chargé de comptes.',
      reasons: [
        {
          title: 'Livré, pas slidé',
          desc: 'Nous mesurons la réussite en mises en production, stores et SLA en ligne — pas en slides.',
        },
        {
          title: 'L’IA dans le produit',
          desc: 'LLM, agents et ML classique là où ils réduisent les coûts ou ouvrent du chiffre — intégrés, évalués, opérés.',
        },
        {
          title: 'Une équipe, de bout en bout',
          desc: 'Design, backend et IA partagent le même sprint et les mêmes points — moins de transferts, moins de surprises.',
        },
        {
          title: 'Un rythme prévisible',
          desc: 'Contrats par phases avec démos hebdomadaires. Vous voyez toujours ce qui est livré, en cours et à venir.',
        },
      ],
    },
    // Services Section
    services: {
      eyebrow: 'Services',
      title: 'Nos Services',
      description: 'Solutions numériques complètes adaptées aux besoins de votre entreprise.',
      items: {
        web: {
          title: 'Développement Web',
          desc: 'Sites web personnalisés, applications web et solutions de commerce électrique construites avec des technologies modernes pour des performances optimales et une expérience utilisateur.',
        },
        mobile: {
          title: 'Applications Mobiles',
          desc: 'Applications mobiles natives et multiplateformes pour iOS et Android avec des expériences utilisateur transparentes et des capacités hors ligne.',
        },
        design: {
          title: 'Design UI/UX',
          desc: 'Solutions de design centrées sur l\'utilisateur qui combinent esthétique et fonctionnalité pour créer des expériences numériques engageantes.',
        },
        ai: {
          title: 'IA / ML',
          desc: 'IA en production : modèles sur mesure, agents LLM, RAG sur vos données, évaluations et garde-fous — intégrés à votre produit, pas seulement en slides.',
        },
        blockchain: {
          title: 'Blockchain',
          desc: 'Smart contracts, intégrations on-chain et infrastructure de production — conçus pour la sécurité, la justesse et l’usage réel.',
        },
        seo: {
          title: 'Optimisation SEO',
          desc: 'Services d\'optimisation des moteurs de recherche pour améliorer vos classements, augmenter le trafic organique et améliorer la visibilité en ligne.',
        },
      },
    },
    // Process Section
    process: {
      eyebrow: 'Processus',
      title: 'Notre Processus',
      description: 'Une approche rationalisée pour fournir des solutions numériques exceptionnelles à temps et dans le budget.',
      phases: {
        discovery: {
          name: 'Découverte',
          time: '1 semaine',
          desc: 'Comprendre vos objectifs commerciaux, votre public cible et les exigences du projet grâce à la recherche et à l\'analyse.',
          items: ['Recherche', 'Analyse', 'Exigences'],
        },
        planning: {
          name: 'Planification',
          time: '2 semaines',
          desc: 'Créer une feuille de route de projet détaillée, des wireframes et un plan stratégique pour guider le processus de développement.',
          items: ['Stratégie', 'Wireframes', 'Feuille de route'],
        },
        execution: {
          name: 'Exécution',
          time: '6 semaines',
          desc: 'Développement agile avec des mises à jour régulières, des tests et des itérations pour donner vie à votre vision.',
          items: ['Développement', 'Tests', 'Itérations'],
        },
        launch: {
          name: 'Lancement',
          time: '1 semaine',
          desc: 'Déploiement final, assurance qualité et support post-lancement pour assurer une livraison réussie du projet.',
          items: ['Déploiement', 'QA', 'Support'],
        },
      },
      phaseLabel: 'PHASE',
    },
    // Portfolio Section
    portfolio: {
      eyebrow: 'Travaux sélectionnés',
      title: 'De vrais produits. De vraies métriques. Pas de maquettes.',
      description: 'Un sous-ensemble de lancements récents — des applications dans les stores, des plateformes en production, des contrats sur mainnet.',
      filters: {
        all: 'Tout le travail',
        ai: 'IA',
        mobile: 'Mobile',
        web: 'Web',
        blockchain: 'Blockchain',
      },
      caseStudy: {
        tag: '// ÉTUDE DE CAS · 2026',
        title: 'Winify — Agent IA qui automatise les réponses RFP',
        meta: 'IA · Web · 14 semaines',
        quote: 'Réduit le temps de réponse RFP de 11 jours à moins de 6 heures. L\'équipe comprenait mieux les approvisionnements entreprises que notre dernier fournisseur.',
        stats: {
          timeSaved: { num: '96%', lbl: 'Temps économisé' },
          winRate: { num: '3.2×', lbl: 'Taux de réussite' },
          pipeline: { num: '$4M', lbl: 'Pipeline débloqué' },
        },
      },
      clients: {
        eyebrow: 'Clients',
        title: 'Ce que disent les fondateurs après notre livraison.',
      },
    },
    // Testimonials Section
    testimonials: {
      project: 'PROJET',
      timeline: 'CHRONOLOGIE',
      outcome: 'RÉSULTAT',
    },
    // FAQ Section
    faq: {
      eyebrow: 'FAQ',
      title: 'Questions courantes, réponses simples.',
      description: 'Vous ne voyez pas votre question? Envoyez-nous un message — nous répondons en 24 heures.',
      cta: 'Posez-nous n\'importe quoi',
      items: {
        q1: {
          q: 'Combien de temps prend un projet typique?',
          a: 'La plupart des engagements durent 10–14 semaines du début à la production. Nous nous engageons sur une date de livraison la première semaine de la découverte, et nous la respectons 98% du temps.',
        },
        q2: {
          q: 'Combien coûte un projet?',
          a: 'Les engagements MVP commencent à $50K. Les constructions de produits complètes se situent généralement entre $80K–$200K. Nous envoyons un SOW à frais fixes après la découverte — pas de facturation horaire.',
        },
        q3: {
          q: 'Travaillez-vous avec des startups, des scale-ups ou des entreprises?',
          a: 'Les trois. Environ la moitié de notre travail concerne des équipes produit de série A–C; l\'autre moitié concerne des groupes d\'innovation d\'entreprise.',
        },
        q4: {
          q: 'Qui fait réellement le travail?',
          a: 'Au minimum, un ingénieur senior, un designer produit et un PM de livraison. Tous ceux que vous rencontrez au début sont dans l\'équipe — nous ne faisons pas de leurre et d\'échange.',
        },
        q5: {
          q: 'Que se passe-t-il après le lancement?',
          a: 'Une garantie de production de 30 jours est incluse. Après cela, des honoraires optionnels ou un package de transfert propre.',
        },
        q6: {
          q: 'Signez-vous des NDAs?',
          a: 'Oui — heureux d\'utiliser le vôtre, ou nous enverrons le nôtre.',
        },
      },
    },
    // Discuss CTA Section
    discuss: {
      eyebrow: 'Parlons',
      title: 'Prêt à construire <span class="accent">quelque chose de réel?</span>',
      description: 'Dites-nous un peu sur le projet. Nous répondrons dans les 24 heures avec les prochaines étapes et un coût estimé.',
      cta: 'Envoyer un briefing de projet',
      email: 'developer@intelapps.ai',
    },
    // 404 Page
    notFound: {
      eyebrow: '— / 404',
      title: 'Page non trouvée.',
      description: 'La page que vous recherchez n\'existe pas.',
      backHome: '← Retour à l\'accueil',
    },
  },
};

// Language getter/setter functions
export function getLang() {
  try {
    return localStorage.getItem('intelapps_lang') || 'en';
  } catch {
    return 'en';
  }
}

export function setLang(l) {
  try {
    localStorage.setItem('intelapps_lang', l);
  } catch {
    // eslint-disable-next-line no-empty
  }
  window.dispatchEvent(new CustomEvent('intelapps:lang', { detail: l }));
}

// Theme functions
export function getTheme() {
  try {
    return localStorage.getItem('intelapps_theme') || 'light';
  } catch {
    return 'light';
  }
}

export function setThemePref(t) {
  try {
    localStorage.setItem('intelapps_theme', t);
  } catch {
    // eslint-disable-next-line no-empty
  }
  document.documentElement.dataset.theme = t;
  window.dispatchEvent(new CustomEvent('intelapps:theme', { detail: t }));
}

export function applyInitialTheme() {
  if (typeof window !== 'undefined') {
    document.documentElement.dataset.theme = getTheme();
  }
}

// Initialize theme on load
if (typeof window !== 'undefined') {
  applyInitialTheme();
}

// I18n Context and Hook
const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(getLang());
  const [theme, setThemeState] = useState(getTheme());

  useEffect(() => {
    const handleLangChange = (e) => setLangState(e.detail);
    const handleThemeChange = (e) => setThemeState(e.detail);
    window.addEventListener('intelapps:lang', handleLangChange);
    window.addEventListener('intelapps:theme', handleThemeChange);
    return () => {
      window.removeEventListener('intelapps:lang', handleLangChange);
      window.removeEventListener('intelapps:theme', handleThemeChange);
    };
  }, []);

  const changeLang = (l) => {
    setLang(l);
    setLangState(l);
  };

  const changeTheme = (t) => {
    setThemePref(t);
    setThemeState(t);
  };

  const t = translations[lang] || translations.en;

  return (
    <I18nContext.Provider
      value={{
        lang,
        theme,
        t,
        changeLang,
        changeTheme,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

I18nProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}
